import { useState, useEffect } from "react";
import { Loader2, AlertTriangle, RefreshCw, Inbox } from "lucide-react";
import { dlq as dlqApi } from "../lib/api";
import type { DLQEntry } from "../lib/api";

export default function DLQ() {
  const [entries, setEntries] = useState<DLQEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  useEffect(() => {
    loadEntries();
  }, []);

  async function loadEntries() {
    setLoading(true);
    try {
      const res = await dlqApi.list();
      setEntries(res.data);
    } catch {}
    setLoading(false);
  }

  async function handleRequeue(id: string) {
    setRetryingId(id);
    try {
      await dlqApi.requeue(id);
      await loadEntries();
    } catch {}
    setRetryingId(null);
  }

  function formatDate(val: string | Date | undefined): string {
    if (!val) return "—";
    return new Date(val).toLocaleString();
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-100">Dead Letter Queue</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Failed queue items that exceeded max retries.
          </p>
        </div>
        <button
          onClick={loadEntries}
          className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs rounded-lg transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 text-gray-600 animate-spin" />
        </div>
      ) : entries.length === 0 ? (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
          <Inbox className="w-8 h-8 text-gray-700 mx-auto mb-2" />
          <p className="text-sm text-gray-600">No failed items in the dead letter queue.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map((entry) => {
            const lastError = entry.errorHistory[entry.errorHistory.length - 1];
            const isAcknowledged = !!entry.acknowledgedAt;
            const isRequeued = !!entry.requeuedAt;

            return (
              <div
                key={entry._id}
                className={`bg-gray-900 border rounded-xl p-4 ${
                  isAcknowledged ? "border-gray-800 opacity-60" : "border-gray-700"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0 space-y-1.5">
                    {/* Queue + task */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-gray-100 truncate">
                        {entry.queueName}
                      </span>
                      <span className="text-xs text-gray-600">task:</span>
                      <span className="text-xs text-gray-400 font-mono truncate">
                        {entry.taskId}
                      </span>
                      {isRequeued && (
                        <span className="text-xs text-green-500 font-medium">requeued</span>
                      )}
                      {isAcknowledged && !isRequeued && (
                        <span className="text-xs text-gray-500 font-medium">acknowledged</span>
                      )}
                    </div>

                    {/* Error message */}
                    {lastError && (
                      <div className="flex items-start gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-red-300 break-words">{lastError.error}</p>
                      </div>
                    )}

                    {/* Meta row */}
                    <div className="flex items-center gap-4 text-xs text-gray-600">
                      <span>Failed: {formatDate(entry.deadAt)}</span>
                      <span>Attempts: {entry.errorHistory.length}</span>
                      {isRequeued && (
                        <span>Requeued: {formatDate(entry.requeuedAt)}</span>
                      )}
                    </div>
                  </div>

                  {/* Retry button */}
                  <button
                    onClick={() => handleRequeue(entry._id)}
                    disabled={retryingId === entry._id || isRequeued}
                    className="flex items-center gap-1 px-3 py-1.5 bg-brand-600 hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs rounded-lg transition-colors flex-shrink-0"
                  >
                    {retryingId === entry._id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3.5 h-3.5" />
                    )}
                    Retry
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
